import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionMenu,
  Autocomplete,
  ConfirmationDialog,
  CopyableText,
  DataGrid,
  Dialog,
  Form,
  FormButton,
  ImageUploader,
  Input,
  SelectInput,
  string,
  useNotifications
} from '@ijoin/components';
import {
  RecordKeepingOrganizationsBillingFrequency,
  UploadAssetBodyContentType,
  useDeleteAdminAdvisoryOrganizationRecordKeepingOrganization,
  useDeleteAdminAnnuityCarrier,
  useDeleteAdminRecordKeepingOrganization,
  useDeleteAdminRecordKeepingOrganizationIntegration,
  useDeleteAdminRecordKeepingOrganizationLicense,
  useGetAdminAnnuityCarrier,
  useGetAdminIntegrations,
  useGetAdminRecordKeeperTypes,
  useGetAdminRecordKeepingOrganization,
  useGetAdminRecordKeepingOrganizationAdvisoryOrganizations,
  useGetAdminRecordKeepingOrganizationIntegrations,
  useGetAdminRecordKeepingOrganizationLicenses,
  useGetAdminRkoLicenseTypes,
  usePatchAdminRecordKeepingOrganization,
  usePostAdminAdvisoryOrganizationRecordKeepingOrganization,
  usePostAdminAnnuityCarrier,
  usePostAdminRecordKeepingOrganizationIntegration,
  usePostAdminRecordKeepingOrganizationLicense,
  useUploadAsset
} from '@ijoin/hooks';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';

import {
  AdvisoryOrganizationAutocomplete,
  LinkButton,
  RkoAdvisoryOrgActionsCell,
  RkoIntegrationActionsCell,
  RkoLicenseActionsCell,
  RkoSsoSection
} from '../../components';
import { assetUrl } from '../../helpers';

type EditFormProps = {
  address: string;
  billingFrequency: string;
  city: string;
  contact: string;
  email: string;
  logoUrl: string;
  name: string;
  phone: string;
  recordKeeperClientId: string;
  recordKeeperTypeId: string;
  recordKeeperUrl: string;
  state: string;
  tiaaManagedSourceId: string;
  tiaaTargetSourceId: string;
  tiaaTrustId: string;
  zip: string;
};

const editResolver = zodResolver(
  z.object({
    address: string(),
    billingFrequency: string(),
    city: string(),
    contact: string(),
    email: string(),
    logoUrl: string(),
    name: string({ maxLength: 100, required: true }),
    phone: string(),
    recordKeeperClientId: string(),
    recordKeeperTypeId: string({ required: true }),
    recordKeeperUrl: string(),
    state: string(),
    tiaaManagedSourceId: string(),
    tiaaTargetSourceId: string(),
    tiaaTrustId: string(),
    zip: string()
  })
);

const billingFrequencyOptions = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' }
];

type Option = { label: string; value: string };

const DEFAULT_TIER_1_PARTICIPANTS = 100000;
const DEFAULT_TIER_1_PRICE = 9;

type AddLicenseFormProps = {
  licenseType: null | Option;
  tier1Participants: null | number;
  tier1Price: null | number;
};

const RecordKeepingOrganizationDetailPage = () => {
  const { recordKeepingOrganizationId } = Route.useParams();
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const navigate = useNavigate();
  const [isEditOpen, , setIsEditOpen] = useToggle(false);
  const [isDeleteOpen, toggleDeleteOpen] = useToggle();
  const [isRemoveAnnuityOpen, toggleRemoveAnnuityOpen] = useToggle();

  const org = useGetAdminRecordKeepingOrganization(recordKeepingOrganizationId);
  const recordKeeperTypes = useGetAdminRecordKeeperTypes();
  const uploadAsset = useUploadAsset({
    mutation: {
      onError: () => notifications.error('Failed to upload logo')
    }
  });
  const patchOrg = usePatchAdminRecordKeepingOrganization({
    mutation: {
      onError: () =>
        notifications.error('Failed to update record keeping organization'),
      onSuccess: () => {
        notifications.success(
          'Record keeping organization updated successfully'
        );
        invalidate();
        setIsEditOpen(false);
      }
    }
  });
  const deleteOrg = useDeleteAdminRecordKeepingOrganization({
    mutation: {
      onError: () =>
        notifications.error('Failed to delete record keeping organization'),
      onSuccess: () => {
        notifications.success('Record keeping organization deleted');
        navigate({ to: '/recordkeepers' });
      }
    }
  });
  const advisoryOrgs =
    useGetAdminRecordKeepingOrganizationAdvisoryOrganizations(
      recordKeepingOrganizationId
    );
  const integrations = useGetAdminRecordKeepingOrganizationIntegrations(
    recordKeepingOrganizationId
  );
  const licenses = useGetAdminRecordKeepingOrganizationLicenses(
    recordKeepingOrganizationId
  );
  const licenseTypes = useGetAdminRkoLicenseTypes();
  const addLicense = usePostAdminRecordKeepingOrganizationLicense({
    mutation: {
      onError: () => notifications.error('Failed to add license'),
      onSuccess: () => {
        invalidate();
        addLicenseForm.reset({
          licenseType: null,
          tier1Participants: DEFAULT_TIER_1_PARTICIPANTS,
          tier1Price: DEFAULT_TIER_1_PRICE
        });
        notifications.success('License added');
      }
    }
  });
  const removeLicense = useDeleteAdminRecordKeepingOrganizationLicense({
    mutation: {
      onError: () => notifications.error('Failed to remove license'),
      onSuccess: () => {
        invalidate();
        notifications.success('License removed');
      }
    }
  });

  const annuityCarrier = useGetAdminAnnuityCarrier(recordKeepingOrganizationId);
  const createAnnuityCarrier = usePostAdminAnnuityCarrier({
    mutation: {
      onError: () =>
        notifications.error('Failed to create annuity carrier integration'),
      onSuccess: () => {
        invalidate();
        notifications.success('Annuity carrier integration created');
      }
    }
  });
  const removeAnnuityCarrier = useDeleteAdminAnnuityCarrier({
    mutation: {
      onError: () =>
        notifications.error('Failed to remove annuity carrier integration'),
      onSuccess: () => {
        invalidate();
        notifications.success('Annuity carrier integration removed');
      }
    }
  });

  const linkAdvisoryOrg =
    usePostAdminAdvisoryOrganizationRecordKeepingOrganization({
      mutation: {
        onError: () =>
          notifications.error('Failed to link advisory organization'),
        onSuccess: () => {
          invalidate();
          linkAdvisoryOrgForm.setValue('advisoryOrg', null);
          notifications.success('Advisory organization linked');
        }
      }
    });
  const linkIntegration = usePostAdminRecordKeepingOrganizationIntegration({
    mutation: {
      onError: () => notifications.error('Failed to link integration'),
      onSuccess: () => {
        invalidate();
        linkIntegrationForm.setValue('integration', null);
        notifications.success('Integration linked');
      }
    }
  });
  const unlinkAdvisoryOrg =
    useDeleteAdminAdvisoryOrganizationRecordKeepingOrganization({
      mutation: {
        onError: () =>
          notifications.error('Failed to unlink advisory organization'),
        onSuccess: () => {
          invalidate();
          notifications.success('Advisory organization unlinked');
        }
      }
    });
  const unlinkIntegration = useDeleteAdminRecordKeepingOrganizationIntegration({
    mutation: {
      onError: () => notifications.error('Failed to unlink integration'),
      onSuccess: () => {
        invalidate();
        notifications.success('Integration unlinked');
      }
    }
  });

  const allIntegrations = useGetAdminIntegrations({ take: 1000 });

  const linkAdvisoryOrgForm = useForm<{ advisoryOrg: null | Option }>({
    defaultValues: { advisoryOrg: null }
  });
  const advisoryOrgToLink = linkAdvisoryOrgForm.watch('advisoryOrg');

  const linkIntegrationForm = useForm<{ integration: null | Option }>({
    defaultValues: { integration: null }
  });
  const integrationToLink = linkIntegrationForm.watch('integration');

  const addLicenseForm = useForm<AddLicenseFormProps>({
    defaultValues: {
      licenseType: null,
      tier1Participants: DEFAULT_TIER_1_PARTICIPANTS,
      tier1Price: DEFAULT_TIER_1_PRICE
    }
  });
  const licenseTypeToAdd = addLicenseForm.watch('licenseType');

  const invalidate = () => {
    queryClient.invalidateQueries({
      predicate: query =>
        query.queryKey.some(
          key =>
            typeof key === 'string' &&
            key.startsWith(
              `/api/v3/admin/record-keeping-organizations/${recordKeepingOrganizationId}`
            )
        )
    });
  };

  const editDefaultValues = {
    address: org.data?.address ?? '',
    billingFrequency: org.data?.billingFrequency ?? '',
    city: org.data?.city ?? '',
    contact: org.data?.contact ?? '',
    email: org.data?.email ?? '',
    logoUrl: org.data?.logoUrl ?? '',
    name: org.data?.name ?? '',
    phone: org.data?.phone ?? '',
    recordKeeperClientId: org.data?.recordKeeperClientId ?? '',
    recordKeeperTypeId: org.data?.recordKeeperTypeId ?? '',
    recordKeeperUrl: org.data?.recordKeeperUrl ?? '',
    state: org.data?.state ?? '',
    tiaaManagedSourceId: org.data?.tiaaManagedSourceId ?? '',
    tiaaTargetSourceId: org.data?.tiaaTargetSourceId ?? '',
    tiaaTrustId: org.data?.tiaaTrustId ?? '',
    zip: org.data?.zip ?? ''
  };

  const editMethods = useForm<EditFormProps>({
    defaultValues: editDefaultValues,
    mode: 'all',
    resolver: editResolver,
    values: editDefaultValues
  });

  const onEditSubmit = (data: EditFormProps) => {
    patchOrg.mutate({
      data: {
        ...data,
        billingFrequency:
          (data.billingFrequency as RecordKeepingOrganizationsBillingFrequency) ||
          undefined
      },
      recordKeepingOrganizationId
    });
  };

  const onCloseEditDialog = () => setIsEditOpen(false);
  const onOpenEditDialog = () => setIsEditOpen(true);

  const editLogoUrl = editMethods.watch('logoUrl');
  const isUploadingLogo = uploadAsset.isPending;

  const onConfirmDelete = () => {
    toggleDeleteOpen();
    deleteOrg.mutate({ recordKeepingOrganizationId });
  };

  const onConfirmRemoveAnnuity = () => {
    toggleRemoveAnnuityOpen();
    removeAnnuityCarrier.mutate({ recordKeepingOrganizationId });
  };

  const onCreateAnnuityCarrier = () =>
    createAnnuityCarrier.mutate({ recordKeepingOrganizationId });

  const onLinkAdvisoryOrg = () => {
    if (!advisoryOrgToLink) return;
    linkAdvisoryOrg.mutate({
      advisoryOrganizationId: advisoryOrgToLink.value,
      recordKeepingOrganizationId
    });
  };

  const onLinkIntegration = () => {
    if (!integrationToLink) return;
    linkIntegration.mutate({
      integrationId: integrationToLink.value,
      recordKeepingOrganizationId
    });
  };

  const onAddLicense = () => {
    const { licenseType, tier1Participants, tier1Price } =
      addLicenseForm.getValues();
    if (!licenseType) return;
    addLicense.mutate({
      data: {
        rkoLicenseTypeId: licenseType.value,
        tier1Participants: tier1Participants ?? undefined,
        tier1Price: tier1Price ?? undefined
      },
      recordKeepingOrganizationId
    });
  };

  const recordKeeperTypeOptions = (recordKeeperTypes.data ?? []).map(type => ({
    label: type.description,
    value: type.recordKeeperTypeId
  }));

  const licenseTypeOptions = (licenseTypes.data ?? [])
    .filter(
      type =>
        !(licenses.data ?? []).some(
          license => license.rkoLicenseTypeId === type.rkoLicenseTypeId
        )
    )
    .map(type => ({
      label: type.description,
      value: type.rkoLicenseTypeId
    }));

  const advisoryOrgColumns: GridColDef[] = [
    {
      field: 'name',
      flex: 2,
      headerName: 'Name',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.name)}
          params={{ advisoryOrganizationId: params.row.advisoryOrganizationId }}
          to='/organizations/$advisoryOrganizationId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'city',
      flex: 1,
      headerName: 'City'
    },
    {
      field: 'state',
      flex: 0.75,
      headerName: 'State'
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <RkoAdvisoryOrgActionsCell
          name={params.row.name}
          onUnlink={() =>
            unlinkAdvisoryOrg.mutate({
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              recordKeepingOrganizationId
            })
          }
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  const integrationColumns: GridColDef[] = [
    {
      field: 'description',
      flex: 2,
      headerName: 'Description',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.description)}
          params={{ integrationId: params.row.integrationId }}
          to='/integrations/$integrationId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'integrationTypeId',
      flex: 1,
      headerName: 'Type',
      valueGetter: (_value, row) =>
        row.IntegrationTypes?.description ?? row.integrationTypeId
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <RkoIntegrationActionsCell
          description={params.row.description}
          onUnlink={() =>
            unlinkIntegration.mutate({
              integrationId: params.row.integrationId,
              recordKeepingOrganizationId
            })
          }
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  const licenseColumns: GridColDef[] = [
    {
      field: 'description',
      flex: 2,
      headerName: 'Type',
      valueGetter: (_value, row) => row.RKOLicenseTypes?.description ?? '-'
    },
    {
      align: 'right',
      field: 'tier1Participants',
      flex: 1,
      headerAlign: 'right',
      headerName: 'Tier 1 Participants'
    },
    {
      align: 'right',
      field: 'tier1Price',
      flex: 1,
      headerAlign: 'right',
      headerName: 'Tier 1 Price'
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <RkoLicenseActionsCell
          description={params.row.RKOLicenseTypes?.description ?? 'license'}
          onRemove={() =>
            removeLicense.mutate({
              recordKeepingOrganizationId,
              rkoLicenseId: params.row.rkoLicenseId
            })
          }
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Breadcrumbs>
        <Link to='/recordkeepers'>Record Keeping Organizations</Link>
        <Typography color='text.primary'>{org.data?.name ?? '...'}</Typography>
      </Breadcrumbs>

      {org.data && (
        <Card sx={{ p: 1 }}>
          <CardContent>
            <Stack
              alignItems='center'
              direction='row'
              justifyContent='space-between'>
              <Stack direction='row' spacing={3} sx={{ flexWrap: 'wrap' }}>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Name
                  </Typography>
                  <Typography variant='h5'>{org.data.name}</Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    City
                  </Typography>
                  <Typography variant='subtitle1'>
                    {org.data.city ?? '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    State
                  </Typography>
                  <Typography variant='subtitle1'>
                    {org.data.state ?? '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Record Keeper Type
                  </Typography>
                  <Typography variant='subtitle1'>
                    {org.data.RecordKeeperTypes?.description ??
                      org.data.recordKeeperTypeId ??
                      '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Organization ID
                  </Typography>
                  <Stack alignItems='center' direction='row' spacing={0.5}>
                    <Typography
                      sx={{ fontFamily: 'monospace' }}
                      variant='body2'>
                      {recordKeepingOrganizationId.slice(0, 8)}…
                    </Typography>
                    <CopyableText value={recordKeepingOrganizationId} />
                  </Stack>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Annuity Carrier RK ID
                  </Typography>
                  <Typography variant='subtitle1'>
                    {annuityCarrier.data?.recordkeeperID ??
                      org.data.annuityCarrierRecordkeeperId ??
                      '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Annexus LIB
                  </Typography>
                  <Typography variant='subtitle1'>
                    {org.data.annexusLibEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                </div>
              </Stack>
              <>
                <ActionMenu
                  actions={[
                    { content: 'Delete', onSelect: toggleDeleteOpen },
                    { content: 'Edit', onSelect: onOpenEditDialog }
                  ]}
                  trigger={
                    <IconButton data-testid='actionsRecordKeepingOrganization'>
                      <MoreVertIcon />
                    </IconButton>
                  }
                />
                <ConfirmationDialog
                  confirmationText='DELETE'
                  confirmColor='error'
                  confirmText='Delete'
                  data-testid='deleteRecordKeepingOrganization'
                  isOpen={isDeleteOpen}
                  message={`Are you sure you want to delete "${org.data.name}"? This action cannot be undone.`}
                  onCancel={toggleDeleteOpen}
                  onConfirm={onConfirmDelete}
                  title='Delete Record Keeping Organization'
                />
              </>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog
        data-testid='editRecordKeepingOrganizationDialog'
        fullWidth
        isOpen={isEditOpen}
        maxWidth='md'
        onClose={onCloseEditDialog}>
        <Form
          methods={editMethods}
          noValidate
          onSubmit={editMethods.handleSubmit(onEditSubmit)}>
          <DialogTitle variant='h3'>
            Edit Record Keeping Organization
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack spacing={2}>
                <Typography variant='h4'>Organization Info</Typography>
                <Divider />
                <Stack
                  alignItems='center'
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={3}>
                  <ImageUploader
                    alt='Logo preview'
                    data-testid='editLogoUrl'
                    emptyPlaceholder='No logo uploaded'
                    invalidTypeMessage='Logo must be a PNG, JPEG, or WebP image.'
                    isUploading={isUploadingLogo}
                    label='Upload logo'
                    onChange={key =>
                      editMethods.setValue('logoUrl', key, {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                    onUpload={({ content, contentType }) =>
                      uploadAsset.mutateAsync({
                        data: {
                          content,
                          contentType: contentType as UploadAssetBodyContentType
                        },
                        kind: 'logos'
                      })
                    }
                    previewSrc={editLogoUrl ? assetUrl(editLogoUrl) : undefined}
                    previewVariant='image'
                    registerCtx={editMethods.register('logoUrl')}
                    value={editLogoUrl}
                  />
                  <Stack flex={1} spacing={2} width='100%'>
                    <Input
                      data-testid='editName'
                      format='string'
                      fullWidth
                      hideError
                      label='Name'
                      name='name'
                      required
                      size='small'
                    />
                    <SelectInput
                      fullWidth
                      hideError
                      label='Record Keeper Type'
                      name='recordKeeperTypeId'
                      options={recordKeeperTypeOptions}
                      placeholder='Select a type'
                      required
                      size='small'
                      variant='outlined'
                    />
                  </Stack>
                </Stack>
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <Input
                    data-testid='editRecordKeeperClientId'
                    format='string'
                    fullWidth
                    hideError
                    label='Record Keeper Client ID'
                    name='recordKeeperClientId'
                    size='small'
                  />
                  <SelectInput
                    fullWidth
                    hideError
                    label='Billing Frequency'
                    name='billingFrequency'
                    options={billingFrequencyOptions}
                    placeholder='Select frequency'
                    size='small'
                    variant='outlined'
                  />
                </Stack>
                <Input
                  data-testid='editRecordKeeperUrl'
                  format='string'
                  fullWidth
                  hideError
                  label='Record Keeper URL'
                  name='recordKeeperUrl'
                  size='small'
                />
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Contact Information</Typography>
                <Divider />
                <Input
                  data-testid='editContact'
                  format='string'
                  fullWidth
                  hideError
                  label='Contact'
                  name='contact'
                  size='small'
                />
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <Input
                    data-testid='editPhone'
                    format='string'
                    fullWidth
                    hideError
                    label='Phone'
                    name='phone'
                    size='small'
                  />
                  <Input
                    data-testid='editEmail'
                    format='string'
                    fullWidth
                    hideError
                    label='Email'
                    name='email'
                    size='small'
                  />
                </Stack>
                <Input
                  data-testid='editAddress'
                  format='string'
                  fullWidth
                  hideError
                  label='Address'
                  name='address'
                  size='small'
                />
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <Input
                    data-testid='editCity'
                    format='string'
                    fullWidth
                    hideError
                    label='City'
                    name='city'
                    size='small'
                  />
                  <Input
                    data-testid='editState'
                    format='string'
                    fullWidth
                    hideError
                    label='State'
                    name='state'
                    size='small'
                  />
                  <Input
                    data-testid='editZip'
                    format='string'
                    fullWidth
                    hideError
                    label='Zip'
                    name='zip'
                    size='small'
                  />
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>TIAA Fields</Typography>
                <Divider />
                <Input
                  data-testid='editTiaaTargetSourceId'
                  format='string'
                  fullWidth
                  hideError
                  label='TIAA Target Source ID'
                  name='tiaaTargetSourceId'
                  size='small'
                />
                <Input
                  data-testid='editTiaaManagedSourceId'
                  format='string'
                  fullWidth
                  hideError
                  label='TIAA Managed Source ID'
                  name='tiaaManagedSourceId'
                  size='small'
                />
                <Input
                  data-testid='editTiaaTrustId'
                  format='string'
                  fullWidth
                  hideError
                  label='TIAA Trust ID'
                  name='tiaaTrustId'
                  size='small'
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelEditRecordKeepingOrganization'
              onClick={onCloseEditDialog}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='submitEditRecordKeepingOrganization'
              disabled={isUploadingLogo || patchOrg.isPending}
              triggerValidation
              variant='contained'>
              Save
            </FormButton>
          </DialogActions>
        </Form>
      </Dialog>

      <Stack spacing={1}>
        <Typography variant='h6'>Linked Advisory Organizations</Typography>
        <Form methods={linkAdvisoryOrgForm}>
          <Stack
            alignItems='flex-end'
            direction='row'
            spacing={1}
            sx={{ mb: 2 }}>
            <AdvisoryOrganizationAutocomplete
              data-testid='advisoryOrgToLink'
              exclude={(advisoryOrgs.data ?? []).map(
                linked => linked.advisoryOrganizationId
              )}
              fullWidth={false}
              name='advisoryOrg'
              sx={{ minWidth: 300 }}
            />
            <Tooltip title='Link'>
              <IconButton
                data-testid='linkAdvisoryOrg'
                disabled={!advisoryOrgToLink}
                onClick={onLinkAdvisoryOrg}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Form>
        <DataGrid
          columns={advisoryOrgColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.advisoryOrganizationId}
          loading={advisoryOrgs.isFetching}
          rows={advisoryOrgs.data ?? []}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'linkedAdvisoryOrganizations'
            }
          }}
        />
      </Stack>

      <Stack spacing={1}>
        <Typography variant='h6'>Linked Integrations</Typography>
        <Form methods={linkIntegrationForm}>
          <Stack
            alignItems='flex-end'
            direction='row'
            spacing={1}
            sx={{ mb: 2 }}>
            <Autocomplete<Option>
              data-testid='integrationToLink'
              hideError
              label='Integration'
              name='integration'
              options={(allIntegrations.data?.data ?? [])
                .filter(
                  integration =>
                    !(integrations.data ?? []).some(
                      linked =>
                        linked.integrationId === integration.integrationId
                    )
                )
                .map(integration => ({
                  label: integration.description,
                  value: integration.integrationId
                }))}
              sx={{ minWidth: 300 }}
            />
            <Tooltip title='Link'>
              <IconButton
                data-testid='linkIntegration'
                disabled={!integrationToLink}
                onClick={onLinkIntegration}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Form>
        <DataGrid
          columns={integrationColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.integrationId}
          loading={integrations.isFetching}
          rows={integrations.data ?? []}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'linkedIntegrations'
            }
          }}
        />
      </Stack>

      <Stack spacing={1}>
        <Typography variant='h6'>Licenses</Typography>
        <Form methods={addLicenseForm}>
          <Stack
            alignItems='flex-end'
            direction='row'
            spacing={1}
            sx={{ mb: 2 }}>
            <Autocomplete<Option>
              data-testid='licenseTypeToAdd'
              hideError
              label='License Type'
              name='licenseType'
              options={licenseTypeOptions}
              sx={{ minWidth: 300 }}
            />
            <Input
              data-testid='addLicenseTier1Participants'
              format='number'
              hideError
              label='Tier 1 Participants'
              name='tier1Participants'
              size='small'
            />
            <Input
              data-testid='addLicenseTier1Price'
              format='currency'
              hideError
              label='Tier 1 Price'
              name='tier1Price'
              size='small'
            />
            <Tooltip title='Add'>
              <IconButton
                data-testid='addLicense'
                disabled={!licenseTypeToAdd || addLicense.isPending}
                onClick={onAddLicense}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Form>
        <DataGrid
          columns={licenseColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.rkoLicenseId}
          loading={licenses.isFetching}
          rows={licenses.data ?? []}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'licenses'
            }
          }}
        />
      </Stack>

      <RkoSsoSection
        recordKeepingOrganizationId={recordKeepingOrganizationId}
      />

      <Stack spacing={1}>
        <Stack alignItems='center' direction='row' spacing={1}>
          <Typography variant='h6'>Annuity Carrier Integration</Typography>
          {!annuityCarrier.data && (
            <Button
              data-testid='createAnnuityCarrier'
              disabled={createAnnuityCarrier.isPending}
              onClick={onCreateAnnuityCarrier}
              variant='outlined'>
              Add Annuity Carrier
            </Button>
          )}
        </Stack>
        {annuityCarrier.data ? (
          <Card sx={{ p: 1 }}>
            <CardContent>
              <Stack direction='row' spacing={3} sx={{ flexWrap: 'wrap' }}>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Recordkeeper Name
                  </Typography>
                  <Typography variant='subtitle1'>
                    {annuityCarrier.data.recordkeeperName ?? '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Recordkeeper ID
                  </Typography>
                  <Typography variant='subtitle1'>
                    {annuityCarrier.data.recordkeeperID}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Contact
                  </Typography>
                  <Typography variant='subtitle1'>
                    {annuityCarrier.data.contactName ?? '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Email
                  </Typography>
                  <Typography variant='subtitle1'>
                    {annuityCarrier.data.contactEmail ?? '-'}
                  </Typography>
                </div>
              </Stack>
              <Button
                color='error'
                data-testid='removeAnnuityCarrierButton'
                onClick={toggleRemoveAnnuityOpen}
                sx={{ mt: 2 }}
                variant='outlined'>
                Remove Integration
              </Button>
              <ConfirmationDialog
                confirmColor='error'
                confirmText='Remove'
                data-testid='removeAnnuityCarrier'
                isOpen={isRemoveAnnuityOpen}
                message='Remove annuity carrier integration from this record keeping organization?'
                onCancel={toggleRemoveAnnuityOpen}
                onConfirm={onConfirmRemoveAnnuity}
                title='Remove Annuity Carrier'
              />
            </CardContent>
          </Card>
        ) : null}
      </Stack>
    </Stack>
  );
};

export const Route = createFileRoute(
  '/recordkeepers/$recordKeepingOrganizationId'
)({
  component: RecordKeepingOrganizationDetailPage
});
